import { useCallback, useEffect, useState } from 'react'

/** D5 belt-and-braces: an eligible tour that never opens must not freeze the demo. */
export const TOUR_NEVER_STARTED_MS = 5000

type Options = {
  // Tour 1's show policy AND a real merchant id — true from the FIRST render, so
  // no transcript frame leaks through the auto-start MutationObserver's async gap.
  tourEligible: boolean
  neverStartedMs?: number
}

type TourPlaybackHold = {
  hold: boolean
  handleTourStart: () => void
  handleTourEnd: () => void
}

// Holds the wizard's guided opening — AI-921's SCRIPTED transcript only, flag
// off there is nothing to hold — until coach tour 1 ends: the merchant presses
// its last step's "Got it", dismisses it (Esc), or it never opens (Option A).
//
//   context: gated on "the tour could still show", never on "the tour has
//   completed". The completed form deadlocks every time the tour never starts:
//   onboarding already finished, tourSeen already true, or merchantId 0.
//   context: per-MOUNT by design, matching WizardTour's own startedRef — leaving
//   the Testing step mid-tour and returning re-arms the tour and the hold together.
//   context: an earlier revision (Option B) released as soon as the merchant
//   pressed step 3's "Okay, show me" — reported as a bug (the demo appeared to
//   start "on step 4" rather than when the merchant actually finished the tour)
//   and reverted. `onStepChange` existed only to serve that mechanism and was
//   removed from WizardTour along with it.
export const useTourPlaybackHold = ({
  tourEligible,
  neverStartedMs = TOUR_NEVER_STARTED_MS,
}: Options): TourPlaybackHold => {
  const [started, setStarted] = useState(false)
  const [ended, setEnded] = useState(false)
  const [neverStarted, setNeverStarted] = useState(false)

  const handleTourStart = useCallback(() => setStarted(true), [])
  const handleTourEnd = useCallback(() => setEnded(true), [])

  // Armed only while an eligible tour has NOT opened. Opening it tears the timer
  // down, so a slow reader is never cut off and nothing is ever pending while the
  // overlay is up. `neverStarted` is in the guard so a fired timer can't re-arm.
  useEffect(() => {
    if (!tourEligible || started || ended || neverStarted) return undefined
    const timer = window.setTimeout(() => setNeverStarted(true), neverStartedMs)
    return () => window.clearTimeout(timer)
  }, [tourEligible, started, ended, neverStarted, neverStartedMs])

  // D1: THE release predicate, deliberately one expression.
  const hold = tourEligible && !ended && (started || !neverStarted)

  return { hold, handleTourStart, handleTourEnd }
}
